/**
 * <tire-finder> web component
 *
 * Renders six dropdowns (Width / Aspect Ratio / Construction / Rim Diameter /
 * Load-Speed Index / Brand) plus Reset and Search buttons. On Search, builds a
 * Shopify-standard `?filter.p.m.custom.<key>=<value>&filter.p.vendor=<brand>`
 * URL and navigates to the configured target collection.
 *
 * State persists to localStorage so the customer can come back to a partly-
 * filled form. Each instance of the component on the page is independent.
 *
 * Authoring contract: option lists are passed in via comma-separated
 * data-attributes on the host element, set in the parent Liquid section.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'forty5_tire_filter';

  // Filter keys map a dropdown's purpose to the Shopify URL param it produces.
  // For metafield filters: `filter.p.m.<namespace>.<key>` — Search & Discovery
  // surfaces these once you've enabled the metafield as a filter.
  // For brand we use the built-in `filter.p.vendor`.
  const FILTERS = [
    { id: 'width', label: 'Width', param: 'filter.p.m.custom.tire_width', attr: 'widthOptions' },
    {
      id: 'aspect',
      label: 'Aspect ratio',
      param: 'filter.p.m.custom.tire_aspect_ratio',
      attr: 'aspectOptions',
    },
    {
      id: 'construction',
      label: 'Construction',
      param: 'filter.p.m.custom.tire_construction',
      attr: 'constructionOptions',
    },
    {
      id: 'rim',
      label: 'Rim diameter',
      param: 'filter.p.m.custom.tire_rim_diameter',
      attr: 'rimOptions',
    },
    {
      id: 'load_speed',
      label: 'Load / Speed',
      param: 'filter.p.m.custom.tire_load_speed_index',
      attr: 'loadSpeedOptions',
    },
    { id: 'brand', label: 'Brand', param: 'filter.p.vendor', attr: 'brandOptions' },
  ];

  function parseOptionList(raw) {
    if (!raw) return [];
    return raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function loadState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (_e) {
      return {};
    }
  }

  function saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_e) {}
  }

  function clearState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_e) {}
  }

  class TireFinder extends HTMLElement {
    constructor() {
      super();
      this._state = loadState();
    }

    connectedCallback() {
      this._render();
    }

    _render() {
      const state = this._state;
      const targetCollection = this.dataset.targetCollection || 'tires';
      const searchLabel = this.dataset.searchLabel || 'Find tires';
      const resetLabel = this.dataset.resetLabel || 'Reset';

      const dropdownsHtml = FILTERS.map((f) => {
        const options = parseOptionList(this.dataset[f.attr]);
        const optionsHtml = options
          .map(
            (opt) =>
              `<option value='${escapeHtml(opt)}' ${state[f.id] === opt ? 'selected' : ''}>${escapeHtml(opt)}</option>`
          )
          .join('');
        return `
          <label class='tire-finder__field'>
            <span>${escapeHtml(f.label)}</span>
            <select data-filter-id='${f.id}' aria-label='${escapeHtml(f.label)}'>
              <option value=''>Any</option>
              ${optionsHtml}
            </select>
          </label>
        `;
      }).join('');

      this.innerHTML = `
        <form class='tire-finder__form' novalidate>
          <div class='tire-finder__grid'>${dropdownsHtml}</div>
          <div class='tire-finder__actions'>
            <button type='button' class='tire-finder__reset'>${escapeHtml(resetLabel)}</button>
            <button type='submit' class='tire-finder__submit button button--primary'>${escapeHtml(searchLabel)}</button>
          </div>
        </form>
      `;

      const form = this.querySelector('form');
      const resetButton = this.querySelector('.tire-finder__reset');
      this.querySelectorAll('select[data-filter-id]').forEach((select) => {
        select.addEventListener('change', (e) => {
          this._state[e.target.dataset.filterId] = e.target.value;
          saveState(this._state);
        });
      });

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._submit(targetCollection);
      });

      resetButton.addEventListener('click', () => {
        this._reset();
      });
    }

    _submit(targetCollection) {
      const params = new URLSearchParams();
      FILTERS.forEach((f) => {
        const v = this._state[f.id];
        if (v) params.append(f.param, v);
      });
      const qs = params.toString();
      const url = `/collections/${encodeURIComponent(targetCollection)}${qs ? `?${qs}` : ''}`;
      window.location.href = url;
    }

    _reset() {
      this._state = {};
      clearState();
      this.querySelectorAll('select[data-filter-id]').forEach((select) => {
        select.value = '';
      });
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  if (!customElements.get('tire-finder')) {
    customElements.define('tire-finder', TireFinder);
  }
})();
